/*
 * DEAL AND MONEY, LABELLED HONESTLY.
 *
 * The old record printed "Retainer $10K/mo" directly above "No fee structure on
 * record" for Pluto, the morning after the win, when the $10K was their ad budget
 * from Dani's intro. That is the bug this module exists to make impossible: one
 * column (companies.monthly_value) holds THEIR ad budget on a prospect and is
 * MEANT to hold our retainer on a client, nothing asks for the retainer at the
 * win, so the number that is there on a client is almost always the budget
 * carried over from the deal (docs/CONVENTIONS.md; memory: "monthly_value has two
 * meanings").
 *
 * So the rows are FIXED and each says where it came from (the brief, item 8):
 *
 *   budget      Their ad budget · theirs, and on a client "carried from the deal"
 *               with "Is this our retainer?". A number never changes its label by
 *               itself when the status changes: there is no "Retainer" row.
 *   quoted      What we quoted, verbatim from the pricing email, kept at every
 *               stage after it went; "In the agreement" when the fee first went
 *               out inside the agreement and no pricing email exists (Pluto).
 *   fee         signed_fee_structure, one line per part. "Fee · from the final
 *               agreement · not signed yet" until a signed copy is on record,
 *               "Signed fee" after (reality.txt [2]).
 *   negotiated  signed_terms_note, typed by hand. Nothing counts redlines.
 *   agreement   the pasted link, or the PandaDoc notification email, never
 *               called "Signed agreement" when it is a mail about the document.
 *   won         the won date and who set it.
 *
 * An empty row says "not recorded" with an Add, once.
 */
import { quoteOnRecord, quoteSourceWords } from "../timelineModel.mjs";
import { shortDate, moneyShort } from "./format.mjs";
import { lastTransition, setByWords, hasSignedCopy } from "./situation.mjs";

export const EMPTY = "not recorded";

// Where a set_value proposal read the number (pending_changes.evidence_source).
const MONEY_SOURCE_WORDS = {
  fireflies: "the call",
  calendar: "the calendar",
  gmail: "an email",
  auditor: "the auditor",
  webflow_form: "the form",
  landing_page: "the landing page",
};

/** "From the Sep 18 call", "From an email, Sep 15", or null when unknown. */
export function moneySourceLine(source, { now = new Date() } = {}) {
  if (!source || !source.evidence_source) return null;
  const when = source.effective_date ? shortDate(source.effective_date, { now }) : null;
  if (source.evidence_source === "fireflies") return when ? `From the ${when} call` : "From the call";
  const what = MONEY_SOURCE_WORDS[source.evidence_source] || source.evidence_source.replace(/_/g, " ");
  return when ? `From ${what}, ${when}` : `From ${what}`;
}

/** The fee as typed: one line per part, blank lines dropped (ui.js signedFeeLines). */
export function feeLines(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * The quote on record and its terms, or null: the first pricing email's, or the
 * newer terms approved from a later email (quoteOnRecord, migration 065). `at` is
 * when the terms shown were sent.
 */
export function latestQuote(timeline, company = null) {
  const q = quoteOnRecord(timeline, company);
  return q ? { ...q, at: q.revised_at || q.sent_at } : null;
}

/*
 * Where "open the agreement" goes and what it is called (ui.js agreementLink, plus
 * the agreement's PandaDoc trail from PR #154): a pasted link is the document; a
 * signed notice or the latest view's notification is a mail ABOUT the document,
 * and says so.
 */
export function agreementLink(company, act) {
  const pasted = pastedUrl(company && company.signed_agreement_url);
  if (pasted) return { url: pasted, label: "Agreement ↗", kind: "document" };
  const notice = company && company.agreement_notice && company.agreement_notice.url;
  if (notice) return { url: notice, label: "PandaDoc notification email ↗", kind: "notice" };
  const doc = act && act.document && act.document.notice_url;
  if (doc) return { url: doc, label: "PandaDoc notification email ↗", kind: "notice" };
  return null;
}

function pastedUrl(value) {
  const s = String(value || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  return `https://${s.replace(/^\/+/, "")}`;
}

/*
 * Whether the fee on record counts as signed. A signed copy on record says so; so
 * does a churn: a company that served its months under the fee and then left was
 * signed, whatever we kept of the paper (the Churned block and frame 1i call it
 * "Signed fee"). The doubt "not signed yet" belongs only to a won deal whose copy
 * has not arrived, which is the case reality.txt [2] is about.
 */
export function feeIsSigned(company, act) {
  return hasSignedCopy(company, act) || !!(company && company.status === "churned");
}

/** The fee row's own label, worked out from what is on record (reality.txt [2]). */
export function feeLabel(company, act) {
  if (!feeLines(company && company.signed_fee_structure).length) return "Fee";
  return feeIsSigned(company, act) ? "Signed fee" : "Fee · from the final agreement · not signed yet";
}

// The situations whose lead block already shows the fee: the rail row points at it
// instead of printing it twice (the brief: "A group leaves out whatever the lead
// block already shows").
const FEE_IN_LEAD_BLOCK = { new_client: "New client", client: "Client", churned: "Churned" };

/**
 * The rows, in order: [{ key, label, value, lines?, sub, empty, add?, link?,
 * pointer?, proposed?, ask? }]. `empty` rows render "not recorded · Add" once.
 */
export function dealRows({ company = null, act = null, timeline = [], situation = null, now = new Date() } = {}) {
  const co = company || {};
  const isClient = co.status === "client" || co.status === "churned";
  const rows = [];

  // Their ad budget. Always theirs; on a client the honest question is asked.
  const budget = co.monthly_value !== null && co.monthly_value !== undefined && co.monthly_value !== "" ? Number(co.monthly_value) : null;
  const source = moneySourceLine(co.money_source, { now });
  rows.push({
    key: "budget",
    label: isClient ? "Their ad budget · theirs · carried from the deal" : "Their ad budget · theirs",
    value: budget === null ? null : `${moneyShort(budget, co.currency || "USD")}/mo`,
    sub: budget === null ? null : source || "source unknown",
    empty: budget === null,
    add: budget === null ? { field: "monthly_value", label: "Add" } : null,
    // On a client the stored figure is meant to be our retainer and nothing asks.
    // The question is the link into the same editor (one Edit per card).
    ask: isClient && budget !== null ? "On a client this field means our retainer. Is this our retainer?" : null,
    proposed: isClient && budget !== null ? "Keep their budget as its own field" : null,
  });

  // What we quoted.
  const quote = latestQuote(timeline, co);
  const toAgreement = lastTransition(co, "pipeline_stage", "agreement");
  if (quote) {
    rows.push({
      key: "quoted",
      label: "What we quoted",
      value: quote.terms.join(" · "),
      lines: quote.terms,
      sub: quoteSourceWords(quote, (ts) => shortDate(ts, { now })),
      empty: false,
      quote,
    });
  } else if (toAgreement) {
    rows.push({
      key: "quoted",
      label: "What we quoted",
      value: `In the agreement · ${shortDate(toAgreement.occurred_at, { now })}`,
      sub: "No pricing email went out",
      empty: false,
    });
  } else {
    rows.push({ key: "quoted", label: "What we quoted", value: null, sub: null, empty: true });
  }

  // The fee.
  const fee = feeLines(co.signed_fee_structure);
  const inLead = FEE_IN_LEAD_BLOCK[situation];
  if (fee.length && inLead) {
    rows.push({
      key: "fee",
      label: "Fee",
      value: feeIsSigned(co, act) ? "Signed" : "From the final agreement, not signed yet",
      sub: null,
      empty: false,
      pointer: { label: `in ${inLead}`, section: "lead_block" },
    });
  } else {
    rows.push({
      key: "fee",
      label: feeLabel(co, act),
      value: fee.length ? fee.join(" · ") : null,
      lines: fee,
      sub: null,
      empty: !fee.length,
      add: fee.length ? null : { field: "signed_fee_structure", label: "Add" },
    });
  }

  // Negotiated.
  const note = typeof co.signed_terms_note === "string" ? co.signed_terms_note.trim() : "";
  rows.push({
    key: "negotiated",
    label: "Negotiated",
    value: note || null,
    sub: null,
    empty: !note,
    add: note ? null : { field: "signed_terms_note", label: "Add" },
  });

  // The agreement.
  const link = agreementLink(co, act);
  rows.push({
    key: "agreement",
    label: "Agreement",
    value: link ? link.label : null,
    link,
    sub: null,
    empty: !link,
    add: link && link.kind === "document" ? null : { field: "signed_agreement_url", label: "Add" },
  });

  // Won on.
  const won = lastTransition(co, "status", "client");
  rows.push({
    key: "won",
    label: "Won on",
    value: co.won_at ? [shortDate(co.won_at, { now }), setByWords(won)].filter(Boolean).join(" · ") : "not won yet",
    sub: null,
    empty: false,
  });

  return rows;
}

/**
 * The group's one-line summary, carrying the money WITH its meaning: "Fee not
 * signed yet · their budget $10K/mo", "$3,500/mo base quoted · their budget
 * $40K/mo", "their budget $10K/mo". Empty string when there is nothing to say.
 */
export function dealSummary(rows) {
  const by = Object.fromEntries((rows || []).map((r) => [r.key, r]));
  const parts = [];
  const fee = by.fee;
  if (fee && !fee.empty) {
    if (/not signed yet/.test(`${fee.label} ${fee.value || ""}`)) parts.push("Fee not signed yet");
    else if (fee.label === "Signed fee" || fee.value === "Signed") parts.push("Fee signed");
  } else if (by.quoted && !by.quoted.empty) {
    const first = by.quoted.lines ? by.quoted.lines[0] : by.quoted.value;
    parts.push(by.quoted.lines ? `${first} quoted` : first);
  }
  if (by.budget && !by.budget.empty) parts.push(`their budget ${by.budget.value}`);
  return parts.join(" · ");
}
